import type { CommonSettingsType } from "../../common-deprecated/settings/fetchCommonSettings";
import type { ModelResultType } from "../../shared-logic/features/filters/utils/constants/resultConstants";
import {
    type ImageOptionsType,
    getCarImage,
    getVisualForGradeImage,
} from "../../common-deprecated/settings/utils/ccisSettingUtils";
import { isLexus } from "../../common-deprecated/settings/utils/commonSettingUtils";
import type { TradeInAemSettingsType } from "../redux/reducers/TradeInAemSettingsReducer";
import {
    TradeInAemDetailFormItemType,
    TradeInAemModelSelectType,
    type TradeInAemNavStep,
    TradeInAemSearchType,
} from "./constants";
import {
    type TradeInAemDetailFormItem,
    type TradeInAemInfoBlockContent,
    type TradeInAemFetchQuoteFunctionReturnType,
    type TradeInAemVehicleWithExtraInfoType,
    LeadCategory,
    TradeInAemFindVehicleStatus,
    type TradeInAemFetchVehicleResultsReturnType,
} from "./types";
import type { QueryBuilderStateType } from "../../common-deprecated/features/new-cars/debug-modal/components/ToolsModal";
import { EnvironmentEnum } from "../../common-deprecated/utils";
import type { TradeInAemDispatchType } from "../redux/store";
import { tradeInAemFindVehicleSetSearchResults } from "../redux/actions/TradeInAemFindVehicleActions";
import {
    tradeInAemSetLatestResponse,
    tradeInAemSetQuote,
    tradeInAemSetQuoteFailed,
    tradeInAenSetVehicleFetching,
} from "../redux/actions/TradeInAemVehicleValueActions";
import { COMPONENT_TRADEIN_AEM_PDF } from "../../shared-logic/server/components";
import type { TradeInCsl } from "../csl/TradeInCsl";

export const isDevMode = (commonSettings: CommonSettingsType): boolean => {
    return !!commonSettings.query.devMode;
};

/**
 * Returns the Trade-in extraQueryBuilderOptions
 */
export const getTradeInAemExtraQueryBuilderOptions = (
    csl: TradeInCsl,
    commonSettings: CommonSettingsType,
): QueryBuilderStateType => {
    return {
        devMode: {
            label: "Trade-in dev mode",
            queryValues: ["true", "false"],
            currentValue: commonSettings.query.devMode || "",
        },
        tradeInStep: {
            label: "Overwrite trade-in step (only works in devMode)",
            queryValues: csl.enabledNavSteps,
            isDropdown: true,
            currentValue: commonSettings.query.tradeInStep || "",
        },
        useTestData: {
            label: "Use trade-in test data",
            queryValues: ["true", "false"],
            currentValue: commonSettings.query.useTestData || "",
        },
    };
};

/**
 * Get the image for a model to be used in the model select list.
 */
export const getModelImage = (
    model: ModelResultType,
    commonSettings: CommonSettingsType,
    options: ImageOptionsType,
    angle = 9,
): string => {
    const { cars, cheapestCar = cars[0] } = model;
    //  TODO: Take a look at why the getVisualForGradeImage does not work and if the getCarImage can be used if needed (OR-5978)
    return isLexus(commonSettings)
        ? getCarImage(commonSettings, "exterior", angle, model.id, cheapestCar.id, options)
        : getVisualForGradeImage(
              commonSettings,
              model.id,
              cheapestCar.grade.id,
              cheapestCar.bodyType.id,
              options,
              angle,
          ).src;
};

/**
 * Create list of models based on provided modelCodes.
 */
export const getFilteredModelList = (modelList: ModelResultType[], modelCodes: string[]): ModelResultType[] => {
    return modelCodes.reduce<ModelResultType[]>((list, modelCode) => {
        const foundModel = modelList.find((listItem) => listItem.code === modelCode);
        if (foundModel) list.push(foundModel);
        return list;
    }, []);
};

/**
 * Get the URL for the trade-in API used for finding and valuating vehicles.
 * See https://cde.toyota-europe.com/confluence/display/IT/Trade-in+API
 * Make sure to always add the correct headers when calling this endpoint. The correct headers can be retrieved from the csl function 'getTradeInApiHeaders'
 */
export const getTradeInAemAPI = (
    commonSettings: CommonSettingsType,
    tradeInAemSettings: TradeInAemSettingsType,
): string => {
    return `${tradeInAemSettings.tradeInGateway}/${commonSettings.country}/${commonSettings.language}`;
};

/**
 * Fetch a vehicle in the find vehicle step. Supports both find by vin/license plate.
 */
export const fetchFindVehicle = (
    value: string,
    type: TradeInAemSearchType,
    commonSettings: CommonSettingsType,
    tradeInAemSettings: TradeInAemSettingsType,
    headers: HeadersInit | undefined,
): Promise<Response> => {
    const api = getTradeInAemAPI(commonSettings, tradeInAemSettings);
    const isVinNumber = type === TradeInAemSearchType.VinNumber;

    const parsedValue = value.replace(/\\s/g, "").toUpperCase();
    const url = `${api}/${isVinNumber ? "vin" : "license-plate"}/${parsedValue}`;
    return fetch(url, { method: "GET", headers });
};

export const fetchVehicleValue = (
    commonSettings: CommonSettingsType,
    tradeInAemSettings: TradeInAemSettingsType,
    vehicle: TradeInAemVehicleWithExtraInfoType,
    headers: HeadersInit | undefined,
): Promise<Response> => {
    const url = `${getTradeInAemAPI(commonSettings, tradeInAemSettings)}/value`;

    return fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(vehicle),
    });
};

/**
 * Helper to retrieve formatted label/values for vehicleDetails formItems.
 */
export const getParsedVehicleDetails = (formItems: {
    [formItemKey: string]: TradeInAemDetailFormItem;
}): TradeInAemInfoBlockContent => {
    const result: TradeInAemInfoBlockContent = [];

    const sortedFormItemKeys = Object.keys(formItems).sort((a, b) => formItems[a].index - formItems[b].index);
    sortedFormItemKeys.forEach((key) => {
        const formItem = formItems[key];

        let parsedValue: string;
        switch (formItem.type) {
            case TradeInAemDetailFormItemType.Checkbox:
                parsedValue =
                    formItem.options
                        ?.filter((option) => option.value === formItem.value)
                        .map((option) => option.label)
                        .join() || "";
                break;

            case TradeInAemDetailFormItemType.Radio:
                parsedValue = formItem.options?.find((option) => option.value === formItem.value)?.label || "";
                break;

            default:
                parsedValue = String(formItem.value);
        }

        result.push({ label: formItem.label, value: parsedValue });
    });

    return result.filter((resultItem) => !!resultItem.value);
};

/**
 * Helper function used to fetch vehicle results from the trade-in API.
 *
 * This function will handle the loading states and other basic stuff that needs to happen for a vehicleFetch, shared across all countries.
 * CSL specific logic is passed with the fetchFn param.
 */
export const handleVehicleFetch = async (
    dispatch: TradeInAemDispatchType,
    fetchFn: () => Promise<TradeInAemFetchVehicleResultsReturnType>,
): Promise<void> => {
    try {
        dispatch(tradeInAemFindVehicleSetSearchResults([], TradeInAemFindVehicleStatus.Loading));
        const { results, status } = await fetchFn();

        dispatch(tradeInAemFindVehicleSetSearchResults(results, status));
    } catch (e) {
        dispatch(tradeInAemFindVehicleSetSearchResults([], TradeInAemFindVehicleStatus.Failed));
    }
};

/**
 * Similar to handleVehicleFetch, this function is a helper to retrieve a quote from the trade-in API.
 */
export const handleQuoteFetch = async (
    dispatch: TradeInAemDispatchType,
    fetchFn: () => TradeInAemFetchQuoteFunctionReturnType,
): Promise<void> => {
    try {
        dispatch(tradeInAenSetVehicleFetching(true));

        const { valuation, latestResponse } = await fetchFn();
        if (valuation) dispatch(tradeInAemSetQuote(valuation));
        else dispatch(tradeInAemSetQuoteFailed(true));
        dispatch(tradeInAemSetLatestResponse(latestResponse));
    } catch (e) {
        dispatch(tradeInAemSetQuoteFailed(true));
    }
};

/**
 * Returns the standalone HTML PDF url used by print pdf and the TradeInAemGetPdfEndpoint
 */
export const getTradeInAemPdfHtmlUrl = (commonSettings: CommonSettingsType, quoteId: string): string => {
    return (
        `${commonSettings.resourcePath}/${commonSettings.country}/${commonSettings.language}/` +
        `${COMPONENT_TRADEIN_AEM_PDF}?quoteId=${quoteId}`
    );
};

/**
 * Returns an url with the actual generated Trade-in PDF.
 * This is an API endpoint on our server (TradeInAemGetPdfEndpoint) which generates an actual pdf out af the standalone HTML PDF url (getTradeInAemPdfHtmlUrl)
 *
 */
export const getTradeInAemPdfUrl = (commonSettings: CommonSettingsType, quoteId: string): string => {
    return (
        `${commonSettings.resourcePath}/${commonSettings.country}/${commonSettings.language}/` +
        `get-tradein-aem-pdf?quoteId=${quoteId}`
    );
};

export const enablePdf = (commonSettings: CommonSettingsType, tradeInAemSettings: TradeInAemSettingsType): boolean => {
    if (commonSettings.environment === EnvironmentEnum.Production) {
        return tradeInAemSettings.enablePdf;
    }

    // enablePdf is added as query parameter for testing purposes outside production
    return tradeInAemSettings.enablePdf || !!commonSettings.query.enablePdf;
};

export const getLastName = (firstLastName: string | undefined, secondLastName: string | undefined): string | null => {
    if (!firstLastName && !secondLastName) return null;

    return `${firstLastName || ""}${firstLastName && secondLastName ? " " : ""}${secondLastName || ""}`;
};

/**
 * Returns the lead category as expected by Ehub based on the car type the user selected
 * The lead category should always contain a value. Ehub requested to always fallback to 'usedCar' (OR-9035)
 */
export const getLeadCategory = (carType: TradeInAemModelSelectType | null): LeadCategory => {
    switch (carType) {
        case TradeInAemModelSelectType.New:
        case TradeInAemModelSelectType.Stock:
            return LeadCategory.NewCar;

        // The default fallback is currently used for 'tradeInAemModelSelectTypes.used', 'tradeInAemModelSelectTypes.none' or null
        default:
            return LeadCategory.UsedCar;
    }
};

/**
 * Formats and returns a value for a text field.
 * Currently only used for Mileage. All dots will be removed from the mileage value.
 */
export const formatVehicleDetailsTextField = (value: string, formType: TradeInAemDetailFormItemType): string => {
    return formType === TradeInAemDetailFormItemType.Mileage
        ? Number(value).toLocaleString().replace(/,/g, ".")
        : value;
};

/**
 * Returns a hard validation rule of type RegExp for a certain formType.
 * If the hardValidationRule is enabled for a certain text field, all non matching text values will be removed from the input.
 * Currently only used for Mileage. Only numeric values are allowed.
 */
export const getHardValidationRule = (formType: TradeInAemDetailFormItemType): RegExp | undefined => {
    switch (formType) {
        case TradeInAemDetailFormItemType.Mileage:
            return new RegExp(/\D/g);
        default:
            return undefined;
    }
};

/**
 * Returns an array of strings which contains all the months in the correct language.
 */
export const getMonths = (country: string, language: string): string[] => {
    const { format } = new Intl.DateTimeFormat(`${language}-${country}`, { month: "long" });
    return [...Array(12).keys()].map((month) => format(new Date(Date.UTC(2024, month % 12))));
};

/**
 * Returns the HTMLInputTypeAttribute corresponding to the TradeInAemDetailFormItemType.
 */
export const getHtmlInputTypeFromFormType = (type: TradeInAemDetailFormItemType): React.HTMLInputTypeAttribute => {
    switch (type) {
        case TradeInAemDetailFormItemType.Text:
        case TradeInAemDetailFormItemType.Mileage:
            return "text";
        case TradeInAemDetailFormItemType.Checkbox:
        case TradeInAemDetailFormItemType.Radio:
            return "button";
        default:
            return "number";
    }
};

// This util checks whether the back CTA should be shown.
// If the currentStep is the first possible step in the configuration, no back CTA should be shown.
export const showBackCtaOnStepComponent = (
    currentStep: TradeInAemNavStep,
    tradeInNavSteps: TradeInAemNavStep[],
): boolean => tradeInNavSteps[0] !== currentStep;
